import { Vector3 } from 'three';
import { el } from './dom';

/** A drone's position on the radar. */
export interface RadarBlip {
  /** The bearing from the camera's forward, radians. Zero is straight ahead. */
  bearing: number;
  /** The distance to the drone, metres. */
  distance: number;
}

/** Most attacker triangles drawn at once; extra simultaneous threats share the ring. */
const THREAT_MARKER_LIMIT = 8;
/** Distance of the triangles from the crosshair, px. */
const THREAT_RING_RADIUS = 46;

/**
 * The heads-up display: crosshair, health, score, objectives and the state
 * indicators that the player needs to read at a glance.
 */
export class Hud {
  readonly root = el('div', 'hud hidden');

  private readonly crosshair = el('div', 'crosshair');
  private readonly healthBar = el('div', 'bar');
  private readonly healthFill = el('div', 'bar-fill');
  private readonly healthText = el('span', 'bar-value', '100');
  private readonly scoreText = el('span', 'stat-value', '0');
  private readonly dronesText = el('span', 'stat-value', '0');
  private readonly modeText = el('span', 'stat-value', 'THIRD');
  private readonly trackingText = el('span', 'stat-value', 'OFF');
  private readonly surfaceText = el('span', 'stat-value', '—');
  private readonly weaponText = el('span', 'stat-value', 'AR');
  private readonly weaponCard = el('div', 'weapon-card ar');
  private readonly weaponName = el('div', 'weapon-name', 'AR');
  private readonly weaponHint = el('div', 'weapon-hint', 'T SWITCH');
  private readonly weaponCooldownFill = el('div', 'weapon-cooldown-fill');
  private readonly bulletTimeOverlay = el('div', 'bullet-time');
  private readonly bulletTimeFill = el('div', 'bullet-time-fill');
  private readonly bulletTimeRemainingText = el('span', 'bullet-time-remaining', '1.5');
  private readonly damageVignette = el('div', 'damage-vignette');
  private readonly pointerPrompt = el('div', 'pointer-prompt', 'CLICK TO CAPTURE MOUSE');
  private readonly sneakIndicator = el('div', 'sneak-indicator', 'SNEAK');
  private readonly detectedIndicator = el('div', 'detected-indicator', 'DETECTED');
  private readonly hullWarning = el('div', 'hull-warning', 'HULL');
  private readonly planePin = el('div', 'plane-pin');
  private planePinText = '';
  private readonly buildPanel = el('div', 'build-panel');
  private readonly buildScanner = el('div', 'build-scanner');
  private buildScannerText = '';
  private readonly noticeText = el('div', 'notice');
  private readonly objectiveText = el('div', 'objective');
  private readonly radar = el('div', 'radar');
  private readonly reticle = el('div', 'reticle');
  private readonly damageIndicator = el('div', 'damage-indicator');
  /** Red triangles around the crosshair, one per drone currently firing. */
  private readonly attackRing = el('div', 'attack-ring');
  private readonly attackTracers: HTMLElement[] = [];
  /** The flash where a bolt lands during bullet time. */
  private readonly boltImpact = el('div', 'bolt-impact');
  private boltImpactTimer: number | null = null;
  /** The full-screen flash when a bolt hits the body, brightest at the hit. */
  private readonly boltHitFlash = el('div', 'bolt-hit-flash');
  private boltHitFlashTimer: number | null = null;
  private noticeTimer: number | null = null;
  private damageTimer: number | null = null;
  private healthFlashTimer: number | null = null;
  private lastHealth = -1;
  private lastMaxHealth = -1;
  private lastScore = -1;
  private lastDrones = -1;
  private lastMode: 'third' | 'first' | null = null;
  private lastObjective = '';
  private lastSurface = '';
  private bulletTimeRemainingValue = 0;
  private readonly blipElements: HTMLElement[] = [];
  private readonly blips: RadarBlip[] = [];

  constructor(parent: HTMLElement) {
    const crosshairLine = el('div', 'crosshair-line');
    const crosshairDot = el('div', 'crosshair-dot');
    this.crosshair.append(crosshairLine, crosshairDot);

    const healthLabel = el('span', 'bar-label', 'HULL');
    this.healthFill.classList.add('health');
    this.healthBar.append(healthLabel, this.healthFill, this.healthText);

    const stats = el('div', 'stats');
    const score = el('div', 'stat');
    score.append(el('span', 'stat-label', 'SCORE'), this.scoreText);
    const drones = el('div', 'stat');
    drones.append(el('span', 'stat-label', 'TARGETS'), this.dronesText);
    const mode = el('div', 'stat');
    mode.append(el('span', 'stat-label', 'VIEW'), this.modeText);
    const tracking = el('div', 'stat');
    tracking.append(el('span', 'stat-label', 'HEAD'), this.trackingText);
    const surface = el('div', 'stat');
    surface.append(el('span', 'stat-label', 'SURFACE'), this.surfaceText);
    const weapon = el('div', 'stat');
    weapon.append(el('span', 'stat-label', 'WEAPON'), this.weaponText);
    stats.append(score, drones, mode, tracking, surface, weapon);

    const weaponCooldown = el('div', 'weapon-cooldown');
    weaponCooldown.append(this.weaponCooldownFill);
    this.weaponCard.append(
      el('span', 'weapon-label', 'WEAPON'),
      this.weaponName,
      this.weaponHint,
      weaponCooldown,
    );

    const bulletRing = el('div', 'bullet-time-ring');
    const bulletRingDelayed = el('div', 'bullet-time-ring delayed');
    const bulletLabel = el('div', 'bullet-time-label', 'BULLET TIME');
    const bulletHint = el('div', 'bullet-time-hint', 'LEAN TO DODGE');
    const bulletBar = el('div', 'bullet-time-bar');
    bulletBar.append(this.bulletTimeFill);
    // The focus frame: a cinematic letterbox of solid black bars top and bottom,
    // softened only at the inner edge. (The engine draws the live frame; these
    // DOM bars are the no-render fallback.)
    const frame = el('div', 'bullet-time-frame');
    frame.append(el('div', 'bullet-time-bar-top'), el('div', 'bullet-time-bar-bottom'));
    this.bulletTimeOverlay.append(
      bulletRing,
      bulletRingDelayed,
      bulletLabel,
      bulletHint,
      bulletBar,
      this.bulletTimeRemainingText,
      frame,
    );

    this.buildPanel.append(
      el('div', 'build-title', 'ROOM BUILDER'),
      this.buildScanner,
      el(
        'div',
        'build-keys',
        'Right mouse place · B piece · J drone · K waypoint · N size · R rotate · M magnetic · X delete · Esc back to the map',
      ),
    );

    this.root.append(
      this.damageVignette,
      this.bulletTimeOverlay,
      this.boltImpact,
      this.boltHitFlash,
      this.buildPanel,
      this.crosshair,
      this.reticle,
      this.attackRing,
      this.damageIndicator,
      this.hullWarning,
      this.planePin,
      this.radar,
      this.healthBar,
      this.weaponCard,
      stats,
      this.objectiveText,
      this.noticeText,
      this.pointerPrompt,
      this.sneakIndicator,
      this.detectedIndicator,
    );
    parent.append(this.root);
  }

  setVisible(visible: boolean): void {
    this.root.classList.toggle('hidden', !visible);
  }

  setHealth(current: number, max: number): void {
    const rounded = Math.round(current);
    if (rounded === this.lastHealth && max === this.lastMaxHealth) return;
    const wasHurt = this.lastHealth >= 0 && rounded < this.lastHealth;
    this.lastHealth = rounded;
    this.lastMaxHealth = max;
    const ratio = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
    this.healthFill.style.width = `${ratio * 100}%`;
    this.healthText.textContent = String(rounded);
    this.root.classList.toggle('low-health', ratio > 0 && ratio <= 0.3);
    if (wasHurt) this.flashHealthBar();
  }

  private flashHealthBar(): void {
    this.healthBar.classList.remove('hit');
    // Force the animation to restart when damage lands on consecutive frames.
    void this.healthBar.offsetWidth;
    this.healthBar.classList.add('hit');
    if (this.healthFlashTimer !== null) window.clearTimeout(this.healthFlashTimer);
    this.healthFlashTimer = window.setTimeout(() => {
      this.healthBar.classList.remove('hit');
      this.healthFlashTimer = null;
    }, 240);
  }

  setScore(score: number): void {
    if (score === this.lastScore) return;
    this.lastScore = score;
    this.scoreText.textContent = String(score);
  }

  setDronesRemaining(remaining: number): void {
    if (remaining === this.lastDrones) return;
    this.lastDrones = remaining;
    this.dronesText.textContent = String(remaining);
  }

  setCameraMode(mode: 'third' | 'first'): void {
    if (mode === this.lastMode) return;
    this.lastMode = mode;
    this.modeText.textContent = mode === 'third' ? 'THIRD' : 'FIRST';
    this.root.classList.toggle('first-person', mode === 'first');
    // The world anchored reticle marks the aim in both modes, because a
    // tracked lean moves the viewpoint: the screen centre is only the aim
    // while the viewer offset is zero, and it is zero when tracking is off.
    this.root.classList.toggle('third-person', mode === 'third');
  }

  /** Show which weapon is equipped (AR or net gun). */
  setWeapon(mode: 'ar' | 'net'): void {
    const label = mode === 'ar' ? 'AR' : 'NET';
    this.weaponText.textContent = label;
    this.weaponName.textContent = label;
    this.weaponHint.textContent = mode === 'net' ? 'SHORT RANGE / T SWITCH' : 'T SWITCH';
    this.weaponCard.classList.toggle('ar', mode === 'ar');
    this.weaponCard.classList.toggle('net', mode === 'net');
  }

  /** Draw the net recharge bar. `ratio` is 0 when ready and 1 when spent. */
  setWeaponCooldown(ratio: number): void {
    const clamped = Math.max(0, Math.min(1, ratio));
    this.weaponCooldownFill.style.width = `${(1 - clamped) * 100}%`;
    this.weaponCard.classList.toggle('cooling', clamped > 0.001);
  }

  /** Toggle the bullet-time vignette and warning. */
  setBulletTime(active: boolean): void {
    this.bulletTimeOverlay.classList.toggle('visible', active);
    this.root.classList.toggle('bullet-time-active', active);
    if (!active) this.bulletTimeRemainingValue = 0;
  }

  /**
   * Where the focus frame is drawn.
   *
   * `canvas` means the engine draws the frame, so real bolts can fly over it;
   * `dom` keeps the CSS bars, which is the fallback for reduced motion and a
   * no-render boot. In canvas mode the DOM bars hide to avoid a double frame.
   */
  setFrameMode(mode: 'dom' | 'canvas'): void {
    this.root.classList.toggle('frame-canvas', mode === 'canvas');
  }

  /** Update the bullet-time countdown bar, seconds. */
  setBulletTimeProgress(remaining: number, duration: number): void {
    this.bulletTimeRemainingValue = Math.max(0, remaining);
    const ratio = duration > 0 ? Math.max(0, Math.min(1, remaining / duration)) : 0;
    this.bulletTimeFill.style.width = `${ratio * 100}%`;
    this.bulletTimeRemainingText.textContent = `${Math.max(0, remaining).toFixed(1)}s`;
  }

  /** Warn that the current aim points at an exterior hull wall. */
  setHullWarning(active: boolean): void {
    this.hullWarning.classList.toggle('visible', active);
  }

  /**
   * Show the hint that the view is waiting to settle onto the wall the body
   * just stepped onto, and how to settle it.
   */
  setPlanePin(visible: boolean, trackingAvailable: boolean): void {
    this.planePin.classList.toggle('visible', visible);
    if (!visible) return;
    const text = trackingAvailable
      ? 'TILT HEAD OR PRESS Q TO SETTLE'
      : 'PRESS Q TO SETTLE ONTO THE WALL';
    if (text === this.planePinText) return;
    this.planePinText = text;
    this.planePin.textContent = text;
  }

  /** True while the settle hint is shown. */
  get planePinVisible(): boolean {
    return this.planePin.classList.contains('visible');
  }

  /**
   * Switch the HUD into room-builder mode.
   *
   * Building is not played: the weapon card, hull bar and objective belong to a
   * firefight, so they step aside for the scanner readout and its keys.
   */
  setBuild(active: boolean, scanner = ''): void {
    this.root.classList.toggle('building', active);
    if (!active) return;
    if (scanner !== this.buildScannerText) {
      this.buildScannerText = scanner;
      this.buildScanner.textContent = scanner;
    }
  }

  /** The scanner readout, for tests and tooling. */
  get buildScannerLabel(): string {
    return this.buildScannerText;
  }

  /** True while the HUD is showing the room builder. */
  get building(): boolean {
    return this.root.classList.contains('building');
  }

  /** Show or hide the click-to-capture prompt. */
  setPointerPrompt(visible: boolean, text = 'CLICK TO CAPTURE MOUSE'): void {
    this.pointerPrompt.textContent = text;
    this.pointerPrompt.classList.toggle('visible', visible);
  }

  /** Show the low, quiet movement state. */
  setSneak(active: boolean): void {
    this.sneakIndicator.classList.toggle('visible', active);
  }

  /** Show that at least one drone has noticed the player. */
  setDetected(active: boolean): void {
    this.detectedIndicator.classList.toggle('visible', active);
  }

  /** Disable the animated damage and bullet-time effects. */
  setReducedMotion(active: boolean): void {
    this.root.classList.toggle('reduced-motion', active);
  }

  /** The weapon readout, for tests and tooling. */
  get weaponLabel(): string {
    return this.weaponText.textContent ?? '';
  }

  /** The large weapon card's label, for tests and tooling. */
  get weaponCardLabel(): string {
    return this.weaponName.textContent ?? '';
  }

  /** True while the bullet-time overlay is shown. */
  get bulletTimeVisible(): boolean {
    return this.bulletTimeOverlay.classList.contains('visible');
  }

  /** Remaining bullet-time time shown by the HUD, seconds. */
  get bulletTimeRemaining(): number {
    return this.bulletTimeRemainingValue;
  }

  /** True while the hull warning is shown. */
  get hullWarningVisible(): boolean {
    return this.hullWarning.classList.contains('visible');
  }

  /** True while the persistent low-health treatment is shown. */
  get lowHealth(): boolean {
    return this.root.classList.contains('low-health');
  }

  /** True while the pointer-capture prompt is shown. */
  get pointerPromptVisible(): boolean {
    return this.pointerPrompt.classList.contains('visible');
  }

  /** True while the sneak indicator is shown. */
  get sneakVisible(): boolean {
    return this.sneakIndicator.classList.contains('visible');
  }

  /** True while the detected indicator is shown. */
  get detectedVisible(): boolean {
    return this.detectedIndicator.classList.contains('visible');
  }

  /** True while the damage vignette is pulsing. */
  get damageVignetteVisible(): boolean {
    return this.damageVignette.classList.contains('pulse');
  }

  /**
   * Place the world anchored reticle.
   *
   * It marks the point the gun is aiming at, so the player can see where the
   * weapon points even when the camera is somewhere else entirely.
   */
  setReticle(x: number, y: number, distance: number, visible: boolean): void {
    this.reticle.classList.toggle('hidden', !visible);
    if (!visible) return;
    this.reticle.style.left = `${x}px`;
    this.reticle.style.top = `${y}px`;
    this.reticle.style.opacity = String(Math.max(0.4, 1 - distance / 70));
  }

  /** The reticle's position, for tests and tooling. */
  get reticlePosition(): { x: number; y: number; visible: boolean } {
    return {
      x: Number.parseFloat(this.reticle.style.left) || 0,
      y: Number.parseFloat(this.reticle.style.top) || 0,
      visible: !this.reticle.classList.contains('hidden'),
    };
  }

  /**
   * Point a short-lived chevron at the source of incoming fire and pulse the
   * full-screen damage vignette.
   *
   * `bearing` is measured from the aim direction, positive to the right, the
   * same convention as the radar.
   */
  showDamageDirection(bearing: number, durationMs = 1100, severity = 1): void {
    this.damageIndicator.style.transform = `rotate(${bearing}rad)`;
    this.damageIndicator.classList.add('visible');
    this.damageVignette.style.setProperty(
      '--damage-severity',
      String(Math.max(0.25, Math.min(1, severity))),
    );
    this.damageVignette.classList.remove('pulse');
    // Restart the vignette animation when another hit lands immediately.
    void this.damageVignette.offsetWidth;
    this.damageVignette.classList.add('pulse');
    this.root.classList.add('damaged');
    if (this.damageTimer !== null) window.clearTimeout(this.damageTimer);
    this.damageTimer = window.setTimeout(() => {
      this.damageIndicator.classList.remove('visible');
      this.damageVignette.classList.remove('pulse');
      this.root.classList.remove('damaged');
      this.damageTimer = null;
    }, durationMs);
  }

  /**
   * Show a small red triangle per incoming direction, ringing the crosshair.
   *
   * `bearings` are measured from the aim direction, positive to the right — the
   * same convention as the damage chevron and the radar — so each triangle
   * points back along the bearing to the drone that is firing. One triangle per
   * attacker, so a pincer reads as two, and an empty list hides the ring.
   */
  setThreatBearings(bearings: readonly number[]): void {
    const max = THREAT_MARKER_LIMIT;
    for (let index = 0; index < bearings.length && index < max; index += 1) {
      const marker = this.threatMarker(index);
      marker.style.transform = `rotate(${bearings[index] ?? 0}rad) translateY(-${THREAT_RING_RADIUS}px)`;
      marker.classList.add('visible');
    }
    for (let index = bearings.length; index < this.attackTracers.length; index += 1) {
      this.attackTracers[index]?.classList.remove('visible');
    }
  }

  /** The visible threat markers, for tests and tooling. */
  get threats(): Array<{ bearing: number; visible: boolean }> {
    return this.attackTracers.map((marker) => ({
      bearing: Number(/rotate\(([-\d.]+)rad\)/.exec(marker.style.transform)?.[1] ?? 0),
      visible: marker.classList.contains('visible'),
    }));
  }

  private threatMarker(index: number): HTMLElement {
    const existing = this.attackTracers[index];
    if (existing) return existing;
    const marker = el('div', 'attack-tracer');
    this.attackRing.append(marker);
    this.attackTracers[index] = marker;
    return marker;
  }

  /** Flash the point where a bolt lands, in screen pixels. */
  showBoltImpact(x: number, y: number, durationMs = 240): void {
    this.boltImpact.style.transform = `translate(${x}px, ${y}px)`;
    this.boltImpact.classList.add('visible');
    if (this.boltImpactTimer !== null) window.clearTimeout(this.boltImpactTimer);
    this.boltImpactTimer = window.setTimeout(() => {
      this.boltImpact.classList.remove('visible');
      this.boltImpactTimer = null;
    }, durationMs);
  }

  /** The impact flash's state, for tests and tooling. */
  get boltImpactState(): { visible: boolean } {
    return { visible: this.boltImpact.classList.contains('visible') };
  }

  /**
   * Flash the screen where a bolt hits the body.
   *
   * `x`/`y` are the hit point in screen pixels, so a bolt coming straight at the
   * camera flashes from the centre outwards. The flash is brightest at the point
   * and washes the whole screen, so a hit is unmistakable; `severity` scales it.
   */
  showBoltHit(x: number, y: number, severity = 1, durationMs = 320): void {
    this.boltHitFlash.style.setProperty('--hit-x', `${x}px`);
    this.boltHitFlash.style.setProperty('--hit-y', `${y}px`);
    this.boltHitFlash.style.setProperty('--hit-severity', String(severity));
    this.boltHitFlash.classList.remove('visible');
    void this.boltHitFlash.offsetWidth;
    this.boltHitFlash.classList.add('visible');
    if (this.boltHitFlashTimer !== null) window.clearTimeout(this.boltHitFlashTimer);
    this.boltHitFlashTimer = window.setTimeout(() => {
      this.boltHitFlash.classList.remove('visible');
      this.boltHitFlashTimer = null;
    }, durationMs);
  }

  /** The screen hit flash's state, for tests and tooling. */
  get boltHitFlashState(): { visible: boolean } {
    return { visible: this.boltHitFlash.classList.contains('visible') };
  }

  /** The damage chevron's state, for tests and tooling. */
  get damageDirection(): { bearing: number; visible: boolean } {
    const match = /rotate\(([-\d.]+)rad\)/.exec(this.damageIndicator.style.transform);
    return {
      bearing: match ? Number(match[1]) : 0,
      visible: this.damageIndicator.classList.contains('visible'),
    };
  }

  setHeadTracking(active: boolean, label?: string): void {
    this.trackingText.textContent = label ?? (active ? 'ON' : 'OFF');
    this.trackingText.classList.toggle('active', active);
  }

  /** The surface the robot is attached to, or null while it is in flight. */
  setSurface(surface: string | null): void {
    const label = surface ? surface.toUpperCase() : '—';
    if (label === this.lastSurface) return;
    this.lastSurface = label;
    this.surfaceText.textContent = label;
  }

  /**
   * The standing objective. It stays on screen until the first drone is
   * destroyed so a new player always knows what the round is about.
   */
  setObjective(text: string): void {
    if (text === this.lastObjective) return;
    this.lastObjective = text;
    this.objectiveText.textContent = text;
    this.objectiveText.classList.toggle('hidden', text.length === 0);
  }

  /**
   * Show where the living drones are.
   *
   * The radar is the visual equivalent of the positional drone hum: a player
   * who cannot hear the audio, or who has the volume off, can still find a
   * threat on a surface they are not facing.
   */
  setRadar(
    positions: readonly Vector3[],
    cameraPosition: Vector3,
    cameraForward: Vector3,
    cameraUp: Vector3,
  ): void {
    this.blips.length = 0;
    for (const position of positions) {
      this.blips.push({
        bearing: bearingFrom(cameraForward, cameraUp, position),
        distance: position.distanceTo(cameraPosition),
      });
    }

    while (this.blipElements.length < this.blips.length) {
      const blip = el('div', 'radar-blip');
      this.radar.append(blip);
      this.blipElements.push(blip);
    }
    while (this.blipElements.length > this.blips.length) {
      this.blipElements.pop()?.remove();
    }
    if (this.blips.length === 0) {
      this.radar.classList.add('hidden');
      return;
    }
    this.radar.classList.remove('hidden');

    for (let index = 0; index < this.blips.length; index += 1) {
      const blip = this.blipElements[index];
      const value = this.blips[index];
      if (!blip || !value) continue;
      blip.style.transform = `translate(-50%, -50%) rotate(${value.bearing}rad) translateY(-26px)`;
      blip.style.opacity = String(Math.max(0.35, 1 - value.distance / 60));
    }
  }

  /** The radar blips, for tests and tooling. */
  get radarBlips(): RadarBlip[] {
    return this.blips.map((blip) => ({ ...blip }));
  }

  /** The surface readout, for tests and tooling. */
  get surfaceLabel(): string {
    return this.surfaceText.textContent ?? '';
  }

  /** The camera mode readout, for tests and tooling. */
  get modeLabel(): string {
    return this.modeText.textContent ?? '';
  }

  /** The head-tracking readout, for tests and tooling. */
  get trackingLabel(): string {
    return this.trackingText.textContent ?? '';
  }

  /** The objective readout, for tests and tooling. */
  get objectiveLabel(): string {
    return this.objectiveText.textContent ?? '';
  }

  notify(text: string, durationMs = 2200): void {
    this.noticeText.textContent = text;
    this.noticeText.classList.add('visible');
    if (this.noticeTimer !== null) window.clearTimeout(this.noticeTimer);
    this.noticeTimer = window.setTimeout(() => {
      this.noticeText.classList.remove('visible');
      this.noticeTimer = null;
    }, durationMs);
  }
}

const scratchToTarget = new Vector3();
const scratchRight = new Vector3();
const scratchForward = new Vector3();
const scratchUp = new Vector3();

/**
 * The bearing of a world position from the camera's forward direction,
 * measured in the plane the camera is looking along. Zero is straight ahead
 * and positive is to the right.
 */
export function bearingFrom(cameraForward: Vector3, cameraUp: Vector3, target: Vector3): number {
  scratchForward.copy(cameraForward);
  if (scratchForward.lengthSq() < 1e-8) return 0;
  scratchForward.normalize();

  scratchUp.copy(cameraUp);
  if (scratchUp.lengthSq() < 1e-8) scratchUp.set(0, 1, 0);
  scratchUp.normalize();

  scratchRight.crossVectors(scratchForward, scratchUp);
  if (scratchRight.lengthSq() < 1e-8) scratchRight.set(1, 0, 0);
  scratchRight.normalize();

  scratchToTarget.copy(target);
  // Project onto the plane the camera is looking along.
  scratchToTarget.addScaledVector(scratchUp, -scratchToTarget.dot(scratchUp));
  if (scratchToTarget.lengthSq() < 1e-8) return 0;
  scratchToTarget.normalize();

  const forward = scratchToTarget.dot(scratchForward);
  const right = scratchToTarget.dot(scratchRight);
  return Math.atan2(right, forward);
}
